<template>
  <div>
    <sidebar></sidebar>
    <div :style="{ marginLeft: sidebarwidth }">
      <div class="px-5 py-5">

        <!-- ── HEADER ──────────────────────────────────────────── -->
        <div class="flex justify-between items-center mb-5">
          <div>
            <h2 class="text-2xl font-bold">User Management</h2>
            <p class="text-sm text-gray-500 mt-1">Manage system users, roles and access.</p>
          </div>
          <button
            v-if="can('add_user')"
            @click="openAddModal"
            class="bg-black text-white px-4 py-2 rounded text-sm font-semibold hover:bg-gray-800"
          >+ Add User</button>
        </div>

        <!-- ── TABS: Active / Deleted ─────────────────────────── -->
        <div class="flex gap-4 mb-4 border-b">
          <button
            @click="activeTab = 'active'; fetchUsers()"
            class="pb-2 text-sm font-semibold border-b-2 transition-colors"
            :class="activeTab === 'active' ? 'border-black text-black' : 'border-transparent text-gray-400 hover:text-gray-600'"
          >
            Active Users
            <span class="ml-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{{ activeUsers.length }}</span>
          </button>
          <button
            @click="activeTab = 'deleted'; fetchDeletedUsers()"
            class="pb-2 text-sm font-semibold border-b-2 transition-colors"
            :class="activeTab === 'deleted' ? 'border-red-500 text-red-500' : 'border-transparent text-gray-400 hover:text-gray-600'"
          >
            Deactivated Users
            <span class="ml-1 px-2 py-0.5 bg-red-50 text-red-400 rounded-full text-xs">{{ deletedUsers.length }}</span>
          </button>
        </div>

        <!-- ── LOADING ─────────────────────────────────────────── -->
        <div v-if="loading" class="py-20 text-center text-gray-400 text-sm">Loading users...</div>

        <!-- ── ACTIVE USERS TABLE ─────────────────────────────── -->
        <div v-else-if="activeTab === 'active'" class="bg-white rounded-lg shadow overflow-hidden">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-white uppercase bg-black">
              <tr>
                <th class="py-3 px-4">Name</th>
                <th class="py-3 px-4">Email</th>
                <th class="py-3 px-4">Phone</th>
                <th class="py-3 px-4">Role</th>
                <th class="py-3 px-4">Department</th>
                <th class="py-3 px-4">Designation</th>
                <th class="py-3 px-4">Status</th>
                <th class="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="activeUsers.length === 0">
                <td colspan="8" class="py-10 text-center text-gray-400">No active users found.</td>
              </tr>
              <tr
                v-for="user in activeUsers"
                :key="user.id"
                class="border-b odd:bg-white even:bg-gray-50"
              >
                <td class="py-3 px-4 font-medium">
                  {{ user.first_name }} {{ user.last_name }}
                  <span v-if="user.is_superuser" class="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">SuperAdmin</span>
                </td>
                <td class="py-3 px-4 text-gray-600">{{ user.email }}</td>
                <td class="py-3 px-4">{{ user.phone || '—' }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                    {{ user.role?.role_name || '—' }}
                  </span>
                </td>
                <td class="py-3 px-4">{{ user.department || '—' }}</td>
                <td class="py-3 px-4">{{ user.designation || '—' }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 rounded text-xs font-medium"
                    :class="user.is_active ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'">
                    {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="py-3 px-4">
                  <div class="flex justify-end gap-2">
                    <button
                      v-if="can('change_user')"
                      @click="openEditModal(user)"
                      class="text-xs px-2 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                    >Edit</button>
                    <button
                      v-if="can('change_user')"
                      @click="openAssignRoleModal(user)"
                      class="text-xs px-2 py-1.5 rounded bg-gray-700 text-white hover:bg-gray-800"
                    >Role</button>
                    <button
                      v-if="can('delete_user') && !user.is_superuser && user.id !== currentUserId"
                      @click="confirmDeactivate(user)"
                      class="text-xs px-2 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
                    >Deactivate</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ── DELETED USERS TABLE ────────────────────────────── -->
        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-white uppercase bg-red-700">
              <tr>
                <th class="py-3 px-4">Name</th>
                <th class="py-3 px-4">Email</th>
                <th class="py-3 px-4">Role</th>
                <th class="py-3 px-4">Deactivated At</th>
                <th class="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="deletedUsers.length === 0">
                <td colspan="5" class="py-10 text-center text-gray-400">No deactivated users.</td>
              </tr>
              <tr
                v-for="user in deletedUsers"
                :key="user.id"
                class="border-b odd:bg-white even:bg-red-50"
              >
                <td class="py-3 px-4 text-gray-500">{{ user.first_name }} {{ user.last_name }}</td>
                <td class="py-3 px-4 text-gray-500">{{ user.email }}</td>
                <td class="py-3 px-4 text-gray-500">{{ user.role?.role_name || '—' }}</td>
                <td class="py-3 px-4 text-gray-400 text-xs">
                  {{ user.deleted_at ? dayjs(user.deleted_at).format('YYYY-MM-DD HH:mm') : '—' }}
                </td>
                <td class="py-3 px-4">
                  <div class="flex justify-end">
                    <button
                      v-if="can('delete_user')"
                      @click="restoreUser(user)"
                      class="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700"
                    >Restore</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════ -->
    <!-- ADD / EDIT USER MODAL                                   -->
    <!-- ════════════════════════════════════════════════════════ -->
    <div v-if="showUserModal" class="popupContainer" @click.self="closeUserModal">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div class="px-6 py-5 border-b sticky top-0 bg-white z-10">
          <h3 class="font-bold text-lg">{{ isEditMode ? 'Edit User' : 'Add New User' }}</h3>
        </div>
        <div class="px-6 py-5 grid grid-cols-2 gap-4">

          <div class="col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Email <span class="text-red-500">*</span></label>
            <input v-model="userForm.email" type="email" :disabled="isEditMode"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500 disabled:bg-gray-100" />
          </div>

          <div v-if="!isEditMode">
            <label class="block text-sm font-medium text-gray-700 mb-1">Password <span class="text-red-500">*</span></label>
            <div class="relative">
              <input v-model="userForm.password" :type="showPassword ? 'text' : 'password'"
                class="w-full border border-gray-300 rounded px-3 py-2 pr-20 text-sm focus:outline-none focus:border-blue-500" />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-gray-600 hover:text-gray-900"
              >
                {{ showPassword ? 'Hide' : 'Show' }}
              </button>
            </div>
          </div>

          <div v-if="!isEditMode">
            <label class="block text-sm font-medium text-gray-700 mb-1">Confirm Password <span class="text-red-500">*</span></label>
            <div class="relative">
              <input v-model="userForm.password_confirm" :type="showConfirmPassword ? 'text' : 'password'"
                class="w-full border border-gray-300 rounded px-3 py-2 pr-20 text-sm focus:outline-none focus:border-blue-500" />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-gray-600 hover:text-gray-900"
              >
                {{ showConfirmPassword ? 'Hide' : 'Show' }}
              </button>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input v-model="userForm.first_name" type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input v-model="userForm.last_name" type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input v-model="userForm.phone" type="tel"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <input v-model="userForm.department" type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Designation</label>
            <input v-model="userForm.designation" type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Role <span class="text-red-500">*</span></label>
            <select v-model="userForm.role"
              required
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
              <option value="" disabled>Select role</option>
              <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.role_name }}</option>
            </select>
          </div>

          <div class="col-span-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="userForm.is_mobile" class="cursor-pointer" />
              <span class="text-sm text-gray-700">Mobile user (can login from mobile app)</span>
            </label>
          </div>

        </div>

        <p v-if="userFormError" class="px-6 pb-2 text-red-500 text-sm">{{ userFormError }}</p>

        <div class="px-6 py-4 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
          <button @click="closeUserModal" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
          <button @click="saveUser" :disabled="savingUser"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-black hover:bg-gray-800 disabled:opacity-60">
            {{ savingUser ? 'Saving...' : (isEditMode ? 'Update User' : 'Create User') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════ -->
    <!-- ASSIGN ROLE MODAL                                       -->
    <!-- ════════════════════════════════════════════════════════ -->
    <div v-if="showRoleModal" class="popupContainer">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 class="font-bold text-lg mb-1">Assign Role</h3>
        <p class="text-sm text-gray-500 mb-4">
          User: <strong>{{ roleTarget?.email }}</strong>
        </p>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Select Role</label>
          <select v-model="assignRoleId"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500">
            <option value="" disabled>Choose a role</option>
            <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.role_name }}</option>
          </select>
        </div>
        <p v-if="roleModalError" class="text-red-500 text-sm mb-3">{{ roleModalError }}</p>
        <div class="flex justify-end gap-3">
          <button @click="showRoleModal = false" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
          <button @click="saveAssignRole" :disabled="savingRole"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-black hover:bg-gray-800 disabled:opacity-60">
            {{ savingRole ? 'Saving...' : 'Assign Role' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════ -->
    <!-- SUCCESS POPUP                                           -->
    <!-- ════════════════════════════════════════════════════════ -->
    <div v-if="showSuccessModal" class="popupContainer" @click.self="showSuccessModal = false">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 class="font-bold text-lg mb-2 text-green-700">User Created</h3>
        <p class="text-sm text-gray-600 mb-5">{{ successModalMessage }}</p>
        <div class="flex justify-end">
          <button
            @click="showSuccessModal = false"
            class="px-4 py-2 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════ -->
    <!-- DEACTIVATE CONFIRMATION MODAL                           -->
    <!-- ════════════════════════════════════════════════════════ -->
    <div v-if="showDeactivateModal" class="popupContainer">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 class="font-bold text-lg mb-2">Deactivate User</h3>
        <p class="text-sm text-gray-600 mb-1">Are you sure you want to deactivate:</p>
        <p class="font-bold text-red-600 mb-2">{{ deactivateTarget?.email }}</p>
        <p class="text-xs text-gray-400 mb-5">
          The user will lose access immediately. You can restore them later.
        </p>
        <div class="flex justify-end gap-3">
          <button @click="showDeactivateModal = false" class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50">Cancel</button>
          <button @click="deactivateUser" :disabled="deactivating"
            class="px-4 py-2 rounded text-sm text-white font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-60">
            {{ deactivating ? 'Deactivating...' : 'Yes, Deactivate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="toastMsg" class="fixed bottom-5 right-5 z-50 bg-white border-l-4 shadow-lg rounded px-5 py-3 text-sm max-w-sm"
      :class="toastType === 'error' ? 'border-red-500' : 'border-green-500'">
      <p class="font-semibold text-gray-800">{{ toastTitle }}</p>
      <p class="text-gray-500 text-xs mt-0.5">{{ toastMsg }}</p>
    </div>

  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { sidebarwidth } from "@/store";
import sidebar from "@/components/sidebar/Sidebar.vue";
import api from "../../boot/axios.js";
import dayjs from "dayjs";
import { can } from "@/auth/permissions";

// ── STATE ──────────────────────────────────────────────────────────────────
const activeUsers  = ref([]);
const deletedUsers = ref([]);
const roles        = ref([]);
const loading      = ref(false);
const activeTab    = ref("active");

// Current logged-in user id — to prevent self-deactivation from UI
const currentUserId = parseInt(localStorage.getItem("id"));

// Add / Edit modal
const showUserModal  = ref(false);
const isEditMode     = ref(false);
const editUserId     = ref(null);
const savingUser     = ref(false);
const userFormError  = ref("");
const userForm       = ref(emptyUserForm());
const showPassword   = ref(false);
const showConfirmPassword = ref(false);

// Assign role modal
const showRoleModal  = ref(false);
const roleTarget     = ref(null);
const assignRoleId   = ref("");
const savingRole     = ref(false);
const roleModalError = ref("");

const showSuccessModal  = ref(false);
const successModalMessage = ref("");

// Deactivate modal
const showDeactivateModal = ref(false);
const deactivateTarget    = ref(null);
const deactivating        = ref(false);

// Toast
const toastMsg   = ref("");
const toastTitle = ref("");
const toastType  = ref("success");
let toastTimer   = null;

function emptyUserForm() {
  return {
    email:            "",
    password:         "",
    password_confirm: "",
    first_name:       "",
    last_name:        "",
    phone:            "",
    department:       "",
    designation:      "",
    role:             "",
    is_mobile:        false,
  };
}

// ── FETCH ──────────────────────────────────────────────────────────────────
const fetchUsers = async () => {
  loading.value = true;
  try {
    const res = await api.get("/auth/user/");
    // ✅ Backend returns { total, users: [...] }
    activeUsers.value = res.data.users || [];
  } catch {
    showToast("error", "Error", "Failed to load users.");
  } finally {
    loading.value = false;
  }
};

const fetchDeletedUsers = async () => {
  loading.value = true;
  try {
    const res = await api.get("/auth/user/deleted-list/");
    // ✅ Backend returns { total, deleted_users: [...] }
    deletedUsers.value = res.data.deleted_users || [];
  } catch {
    showToast("error", "Error", "Failed to load deactivated users.");
  } finally {
    loading.value = false;
  }
};

const fetchRoles = async () => {
  try {
    const res = await api.get("/auth/role/");
    roles.value = res.data?.results ?? (Array.isArray(res.data) ? res.data : []);
  } catch {
    showToast("error", "Error", "Failed to load roles.");
  }
};

// ── ADD / EDIT USER ────────────────────────────────────────────────────────
const openAddModal = () => {
  isEditMode.value   = false;
  editUserId.value   = null;
  userForm.value     = emptyUserForm();
  userFormError.value = "";
  showPassword.value = false;
  showConfirmPassword.value = false;
  showUserModal.value = true;
};

const openEditModal = (user) => {
  isEditMode.value   = true;
  editUserId.value   = user.id;
  userFormError.value = "";
  userForm.value     = {
    email:       user.email       || "",
    first_name:  user.first_name  || "",
    last_name:   user.last_name   || "",
    phone:       user.phone       || "",
    department:  user.department  || "",
    designation: user.designation || "",
    role:        user.role?.id    || "",
    is_mobile:   user.is_mobile   || false,
    // password fields not shown on edit
    password:         "",
    password_confirm: "",
  };
  showPassword.value = false;
  showConfirmPassword.value = false;
  showUserModal.value = true;
};

const closeUserModal = () => {
  showUserModal.value = false;
  userFormError.value = "";
  showPassword.value = false;
  showConfirmPassword.value = false;
};

const saveUser = async () => {
  userFormError.value = "";

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

  if (!isEditMode.value) {
    // Create validations
    if (!userForm.value.email)    { userFormError.value = "Email is required."; return; }
    if (!isValidEmail(userForm.value.email)) {
      userFormError.value = "Please enter a valid email address.";
      return;
    }
    if (!userForm.value.role) {
      userFormError.value = "Role is required.";
      return;
    }
    if (!userForm.value.password) { userFormError.value = "Password is required."; return; }
    if (userForm.value.password !== userForm.value.password_confirm) {
      userFormError.value = "Passwords do not match."; return;
    }
    if (userForm.value.password.length < 8) {
      userFormError.value = "Password must be at least 8 characters."; return;
    }
  }

  savingUser.value = true;
  try {
    if (isEditMode.value) {
      // ✅ PUT /auth/user/<id>/ — SuperAdmin endpoint, drops password
      const payload = { ...userForm.value };
      delete payload.password;
      delete payload.password_confirm;

      const res = await api.put(`/auth/user/${editUserId.value}/`, payload);
      if (res.data.status === true) {
        showToast("success", "Updated", `User "${userForm.value.email}" updated.`);
        closeUserModal();
        fetchUsers();
      } else {
        userFormError.value = res.data.message || "Update failed.";
      }
    } else {
      // ✅ POST /auth/user/ — SuperAdmin create endpoint
      const res = await api.post("/auth/user/", userForm.value);
      if (res.status === 201 && res.data.status === true) {
        successModalMessage.value = `User "${userForm.value.email}" was created successfully.`;
        showSuccessModal.value = true;
        showToast("success", "Created", `User "${userForm.value.email}" created.`);
        closeUserModal();
        fetchUsers();
      } else {
        userFormError.value = res.data.message || "Create failed.";
      }
    }
  } catch (err) {
    const msg = err?.response?.data
      ? Object.values(err.response.data).flat().join(" ")
      : "Something went wrong.";
    userFormError.value = msg;
  } finally {
    savingUser.value = false;
  }
};

// ── ASSIGN ROLE ────────────────────────────────────────────────────────────
const openAssignRoleModal = (user) => {
  roleTarget.value    = user;
  assignRoleId.value  = user.role?.id || "";
  roleModalError.value = "";
  showRoleModal.value = true;
};

const saveAssignRole = async () => {
  if (!assignRoleId.value) { roleModalError.value = "Please select a role."; return; }
  savingRole.value = true;
  try {
    const res = await api.post(`/auth/user/${roleTarget.value.id}/assign-role/`, {
      role_id: assignRoleId.value,
    });
    if (res.data.status === true) {
      showToast("success", "Role Assigned", res.data.message);
      showRoleModal.value = false;
      fetchUsers();
    } else {
      roleModalError.value = res.data.message || "Failed to assign role.";
    }
  } catch {
    roleModalError.value = "Something went wrong.";
  } finally {
    savingRole.value = false;
  }
};

// ── DEACTIVATE ─────────────────────────────────────────────────────────────
const confirmDeactivate = (user) => {
  deactivateTarget.value  = user;
  showDeactivateModal.value = true;
};

const deactivateUser = async () => {
  deactivating.value = true;
  try {
    const res = await api.delete(`/auth/user/${deactivateTarget.value.id}/`);
    if (res.data.status === true) {
      showToast("success", "Deactivated", res.data.message);
      showDeactivateModal.value = false;
      deactivateTarget.value    = null;
      fetchDeletedUsers();
      fetchUsers();
    } else {
      showToast("error", "Error", res.data.message || "Deactivation failed.");
      showDeactivateModal.value = false;
    }
  } catch (err) {
    showToast("error", "Error", err?.response?.data?.message || "Deactivation failed.");
    showDeactivateModal.value = false;
  } finally {
    deactivating.value = false;
  }
};

// ── RESTORE ────────────────────────────────────────────────────────────────
const restoreUser = async (user) => {
  try {
    const res = await api.post(`/auth/user/${user.id}/restore/`);
    if (res.data.status === true) {
      showToast("success", "Restored", res.data.message);
      fetchDeletedUsers();
      // Refresh active count too
      fetchUsers();
    } else {
      showToast("error", "Error", res.data.message || "Restore failed.");
    }
  } catch {
    showToast("error", "Error", "Restore failed.");
  }
};

// ── TOAST ──────────────────────────────────────────────────────────────────
const showToast = (type, title, msg) => {
  toastType.value  = type;
  toastTitle.value = title;
  toastMsg.value   = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toastMsg.value = ""; }, 3500);
};

// ── INIT ───────────────────────────────────────────────────────────────────
onMounted(() => {
  fetchUsers();
  fetchRoles();
});
</script>

<style scoped>
.popupContainer {
  position: fixed;
  top: 0; bottom: 0; left: 0; right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 50;
}
</style>